import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import apiService from '../services/apiService';

interface MonthlyChartData {
  month: string;
  total: number;
  label: string;
}

interface MonthlyChartStats {
  total_amount: number;
  average_monthly: number;
  highest_month: number;
}

export const useMonthlyChart = (currency: string, startDate: Date | null, endDate: Date | null, excludeBigExpenses: boolean = false) => {
  const [monthlyChartData, setMonthlyChartData] = useState<MonthlyChartData[]>([]);
  const [monthlyChartStats, setMonthlyChartStats] = useState<MonthlyChartStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadMonthlyChartData = async () => {
      try {
        setLoading(true);
        setError(null);

        // If dates provided, use them; otherwise backend will return all-time data
        const startDateStr = startDate ? format(startDate, 'yyyy-MM-dd') : undefined;
        const endDateStr = endDate ? format(endDate, 'yyyy-MM-dd') : undefined;

        const response = await apiService.getMonthlyExpenses(currency, startDateStr, endDateStr, excludeBigExpenses);

        if (cancelled) return;

        setMonthlyChartData(response.monthly_data || []);
        setMonthlyChartStats(response.stats || null);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading monthly chart data:', err);
        setError(err as Error);
        setMonthlyChartData([]);
        setMonthlyChartStats(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadMonthlyChartData();

    return () => {
      cancelled = true;
    };
  }, [currency, startDate, endDate, excludeBigExpenses]);

  return { monthlyChartData, monthlyChartStats, loading, error };
};
